import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  clearCart as clearOnDemandCart,
  fetchCart as fetchOnDemandCart,
  removeFromCart as removeOnDemandCartItem,
} from '../services/onDemandLearningService';
import {
  clearBookingCart,
  fetchBookingCart,
  removeBookingCartItem,
} from '../services/bookingCartService';
import { fetchLearners } from '../services/studentAccountService';
import { tutorFirstName } from '../utils/tutorLearnerDisplay';
import DeleteConfirmModal from './Notifications/DeleteConfirmModal';
import TutorProfileAvatar from './TutorProfileAvatar';

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

const itemPrice = (item) =>
  Number(item.pricePerMinute || 0) * Number(item.durationMinutes || 0);

const sessionDuration = (startTime, endTime) => {
  const minutes = Math.max(
    Math.round((new Date(endTime) - new Date(startTime)) / 60000),
    0,
  );
  if (minutes < 60) return `${minutes} min`;
  const hours = minutes / 60;
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)} hr`;
};

const sessionDate = (value) =>
  new Date(value).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const sessionTime = (startTime, endTime) => {
  const format = (value) =>
    new Date(value).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  return `${format(startTime)} – ${format(endTime)}`;
};

const OnDemandCartPage = () => {
  const navigate = useNavigate();
  const [sessionCart, setSessionCart] = useState({ items: [], count: 0 });
  const [onDemandCart, setOnDemandCart] = useState({ items: [], count: 0 });
  const [learners, setLearners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [sessions, onDemand, learnerResponse] = await Promise.all([
        fetchBookingCart(),
        fetchOnDemandCart(),
        fetchLearners().catch(() => ({ learners: [] })),
      ]);
      setSessionCart({
        items: sessions?.items || [],
        count: sessions?.count ?? sessions?.items?.length ?? 0,
      });
      setOnDemandCart({
        items: onDemand?.items || [],
        count: onDemand?.count ?? onDemand?.items?.length ?? 0,
      });
      setLearners(
        Array.isArray(learnerResponse)
          ? learnerResponse
          : learnerResponse?.learners || [],
      );
    } catch {
      setError('Unable to load your cart. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const learnerNameByLabel = useMemo(
    () =>
      new Map(
        learners.map((learner) => [
          learner.learnerLabel,
          learner.learnerFirstName || learner.learnerLabel,
        ]),
      ),
    [learners],
  );

  const groups = useMemo(() => {
    const map = new Map();
    const getGroup = (learnerLabel) => {
      const label = learnerLabel || 'Self';
      if (!map.has(label)) {
        map.set(label, {
          learnerLabel: label,
          learnerName: learnerNameByLabel.get(label) || label,
          sessions: [],
          onDemand: [],
        });
      }
      return map.get(label);
    };
    sessionCart.items.forEach((item) =>
      getGroup(item.learnerLabel).sessions.push(item),
    );
    onDemandCart.items.forEach((item) =>
      getGroup(item.learnerLabel).onDemand.push(item),
    );
    return [...map.values()];
  }, [sessionCart.items, onDemandCart.items, learnerNameByLabel]);

  const sessionTotal = sessionCart.items.reduce(
    (sum, item) => sum + Number(item.totalPrice || 0),
    0,
  );
  const onDemandTotal = onDemandCart.items.reduce(
    (sum, item) => sum + itemPrice(item),
    0,
  );
  const grandTotal = sessionTotal + onDemandTotal;
  const itemCount = sessionCart.items.length + onDemandCart.items.length;

  const handleRemoveSession = async (cartItemId) => {
    try {
      const response = await removeBookingCartItem(cartItemId);
      setSessionCart({
        items: response?.items || [],
        count: response?.count ?? response?.items?.length ?? 0,
      });
      return true;
    } catch {
      setError('Unable to remove the session.');
      return false;
    }
  };

  const handleRemoveOnDemand = async (cartItemId) => {
    try {
      const response = await removeOnDemandCartItem(cartItemId);
      setOnDemandCart({
        items: response?.items || [],
        count: response?.count ?? response?.items?.length ?? 0,
      });
      return true;
    } catch {
      setError('Unable to remove the content.');
      return false;
    }
  };

  const handleClear = async () => {
    try {
      await Promise.all([clearBookingCart(), clearOnDemandCart()]);
      setSessionCart({ items: [], count: 0 });
      setOnDemandCart({ items: [], count: 0 });
      return true;
    } catch {
      setError('Unable to clear the cart.');
      await load();
      return false;
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete || deleteBusy) return;

    setDeleteBusy(true);
    setError('');
    let succeeded = false;

    if (pendingDelete.type === 'session') {
      succeeded = await handleRemoveSession(pendingDelete.id);
    } else if (pendingDelete.type === 'onDemand') {
      succeeded = await handleRemoveOnDemand(pendingDelete.id);
    } else if (pendingDelete.type === 'clear') {
      succeeded = await handleClear();
    }

    setDeleteBusy(false);
    if (succeeded) {
      setPendingDelete(null);
    }
  };

  const confirmationCopy = useMemo(() => {
    if (pendingDelete?.type === 'clear') {
      return {
        title: 'Clear your cart?',
        message:
          'Are you sure you want to remove all sessions and On-Demand Learning items from your cart? This action cannot be undone.',
        confirmLabel: 'Clear Cart',
      };
    }

    return {
      title: 'Remove this item?',
      message: pendingDelete?.label
        ? `Are you sure you want to remove “${pendingDelete.label}” from your cart?`
        : 'Are you sure you want to remove this item from your cart?',
      confirmLabel: 'Remove',
    };
  }, [pendingDelete]);

  const handleDownloadPdf = async () => {
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      const pageHeight = doc.internal.pageSize.getHeight();
      let y = 18;
      const ensureSpace = (height = 10) => {
        if (y + height <= pageHeight - 15) return;
        doc.addPage();
        y = 18;
      };
      const line = (text, options = {}) => {
        const { size = 10, bold = false, indent = 0, spacing = 6 } = options;
        ensureSpace(spacing + 4);
        doc.setFont('helvetica', bold ? 'bold' : 'normal');
        doc.setFontSize(size);
        const lines = doc.splitTextToSize(String(text), 180 - indent);
        doc.text(lines, 15 + indent, y);
        y += lines.length * spacing;
      };

      line('Tutor Scene — Your Cart', { size: 18, bold: true, spacing: 8 });
      line(`Generated ${new Date().toLocaleString()}`, {
        size: 9,
        spacing: 7,
      });

      groups.forEach((group) => {
        ensureSpace(22);
        line(group.learnerName, { size: 14, bold: true, spacing: 8 });
        if (group.sessions.length) {
          line('Sessions', { size: 11, bold: true });
          group.sessions.forEach((session) => {
            line(
              `${tutorFirstName(session.tutorName)} — ${session.subjectName}`,
              { indent: 4, bold: true },
            );
            line(
              `${sessionDate(session.startTime)} | ${sessionTime(
                session.startTime,
                session.endTime,
              )} | ${sessionDuration(
                session.startTime,
                session.endTime,
              )} | ${money(session.totalPrice)}`,
              { indent: 8, size: 9 },
            );
          });
        }
        if (group.onDemand.length) {
          line('On-Demand Learning', { size: 11, bold: true });
          group.onDemand.forEach((item) => {
            line(
              `${item.title} — ${tutorFirstName(item.tutorName)} — ${money(
                itemPrice(item),
              )}`,
              { indent: 4 },
            );
          });
        }
        const subtotal =
          group.sessions.reduce(
            (sum, item) => sum + Number(item.totalPrice || 0),
            0,
          ) +
          group.onDemand.reduce((sum, item) => sum + itemPrice(item), 0);
        line(`Learner Subtotal: ${money(subtotal)}`, {
          bold: true,
          spacing: 9,
        });
      });

      line(`Cart Total: ${money(grandTotal)}`, {
        size: 14,
        bold: true,
        spacing: 8,
      });
      doc.save('Tutor-Scene-Cart.pdf');
    } catch {
      setError('Unable to generate the PDF. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="bg-gradient-to-r from-brand-dark-blue to-brand-medium-blue py-12 text-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4">
          <div>
            <h1 className="text-2xl font-bold md:text-3xl">Your Cart</h1>
            <p className="mt-1 text-sm text-white/75">
              Review sessions and On-Demand Learning by learner.
            </p>
          </div>
          <Link
            to="/on-demand-learning"
            className="text-sm font-medium text-brand-light-blue hover:text-white"
          >
            ← Back to On-Demand Learning
          </Link>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-8">
        {error && (
          <div className="mb-5 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <p className="py-12 text-center text-gray-500">Loading cart…</p>
        ) : itemCount === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-white p-10 text-center">
            <p className="mb-4 text-gray-600">Your cart is empty.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/book-session"
                className="rounded-lg bg-brand-medium-blue px-6 py-2 font-medium text-white"
              >
                Book a Session
              </Link>
              <Link
                to="/on-demand-learning"
                className="rounded-lg border border-gray-300 px-6 py-2 font-medium text-gray-700"
              >
                Browse Lessons
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="space-y-6">
              {groups.map((group) => {
                const subtotal =
                  group.sessions.reduce(
                    (sum, item) => sum + Number(item.totalPrice || 0),
                    0,
                  ) +
                  group.onDemand.reduce(
                    (sum, item) => sum + itemPrice(item),
                    0,
                  );
                return (
                  <section
                    key={group.learnerLabel}
                    className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
                  >
                    <div className="flex items-center justify-between bg-blue-50 px-5 py-4">
                      <h2 className="text-lg font-bold text-brand-dark-blue">
                        {group.learnerName}
                      </h2>
                      <span className="text-sm font-semibold text-brand-medium-blue">
                        {group.sessions.length + group.onDemand.length} item
                        {group.sessions.length + group.onDemand.length !== 1
                          ? 's'
                          : ''}
                      </span>
                    </div>

                    {group.sessions.length > 0 && (
                      <div className="border-b border-gray-200 p-5">
                        <h3 className="mb-4 font-bold text-gray-900">Sessions</h3>
                        <div className="space-y-4">
                          {group.sessions.map((session) => (
                            <div
                              key={session.id}
                              className="rounded-lg border border-gray-200 p-4"
                            >
                              <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                                <TutorProfileAvatar
                                  profilePic={session.tutorAvatarUrl}
                                  name={session.tutorName}
                                  className="h-12 w-12 border border-gray-200"
                                  showInitialFallback
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap justify-between gap-2">
                                    <div>
                                      <p className="font-bold text-brand-dark-blue">
                                        {tutorFirstName(session.tutorName)}
                                      </p>
                                      <p className="text-sm text-gray-500">
                                        {session.subjectName}
                                      </p>
                                    </div>
                                    <p className="font-bold text-gray-900">
                                      {money(session.totalPrice)}
                                    </p>
                                  </div>
                                  <div className="mt-3 grid gap-1 text-sm text-gray-600 sm:grid-cols-3">
                                    <span>{sessionDate(session.startTime)}</span>
                                    <span>
                                      {sessionTime(
                                        session.startTime,
                                        session.endTime,
                                      )}
                                    </span>
                                    <span>
                                      {sessionDuration(
                                        session.startTime,
                                        session.endTime,
                                      )}
                                    </span>
                                  </div>
                                  <div className="mt-4 flex gap-4 text-sm font-semibold">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        navigate(
                                          `/tutor/${
                                            session.tutorProfileId
                                          }?cartItem=${session.id}&subject=${encodeURIComponent(
                                            session.subjectName,
                                          )}`,
                                        )
                                      }
                                      className="text-brand-medium-blue hover:underline"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setPendingDelete({
                                          type: 'session',
                                          id: session.id,
                                          label: `${session.subjectName} session with ${tutorFirstName(
                                            session.tutorName,
                                          )}`,
                                        })
                                      }
                                      className="text-red-600 hover:underline"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {group.onDemand.length > 0 && (
                      <div className="border-b border-gray-200 p-5">
                        <h3 className="mb-4 font-bold text-gray-900">
                          On-Demand Learning
                        </h3>
                        <div className="space-y-4">
                          {group.onDemand.map((item) => (
                            <div
                              key={item.cartItemId}
                              className="flex flex-col gap-4 rounded-lg border border-gray-200 p-4 sm:flex-row sm:items-center"
                            >
                              <TutorProfileAvatar
                                profilePic={item.tutorAvatarUrl}
                                name={item.tutorName}
                                className="h-12 w-12 border border-gray-200"
                                showInitialFallback
                              />
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap justify-between gap-2">
                                  <div>
                                    <p className="font-bold text-brand-dark-blue">
                                      {item.title}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                      By {tutorFirstName(item.tutorName)}
                                    </p>
                                  </div>
                                  <p className="font-bold text-gray-900">
                                    {money(itemPrice(item))}
                                  </p>
                                </div>
                                <div className="mt-3 flex gap-4 text-sm font-semibold">
                                  <Link
                                    to={`/on-demand-learning?type=${encodeURIComponent(
                                      item.contentType,
                                    )}&content=${item.id}`}
                                    className="text-brand-medium-blue hover:underline"
                                  >
                                    Edit
                                  </Link>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setPendingDelete({
                                        type: 'onDemand',
                                        id: item.cartItemId,
                                        label: item.title,
                                      })
                                    }
                                    className="text-red-600 hover:underline"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between px-5 py-4">
                      <span className="font-semibold text-gray-700">
                        Learner&apos;s Subtotal
                      </span>
                      <span className="text-lg font-bold text-brand-dark-blue">
                        {money(subtotal)}
                      </span>
                    </div>
                  </section>
                );
              })}

              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="w-full rounded-xl border border-brand-medium-blue bg-white px-5 py-3 font-semibold text-brand-medium-blue hover:bg-blue-50"
              >
                + Add Another Learner
              </button>
            </div>

            <aside>
              <div className="sticky top-24 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-bold text-brand-dark-blue">
                  Order Summary
                </h2>
                <div className="mt-5 space-y-3 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Sessions</span>
                    <span>{money(sessionTotal)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>On-Demand Learning</span>
                    <span>{money(onDemandTotal)}</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 pt-4 text-lg font-bold text-gray-900">
                    <span>Total</span>
                    <span>{money(grandTotal)}</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleDownloadPdf}
                  className="mt-6 w-full rounded-lg bg-brand-medium-blue px-4 py-3 font-semibold text-white hover:bg-brand-dark-blue"
                >
                  Download Cart as PDF
                </button>
                <button
                  type="button"
                  disabled
                  title="Checkout coming soon"
                  className="mt-3 w-full cursor-not-allowed rounded-lg bg-brand-gold px-4 py-3 font-bold text-brand-dark-blue opacity-70"
                >
                  Checkout (coming soon)
                </button>
                <button
                  type="button"
                  onClick={() => setPendingDelete({ type: 'clear' })}
                  className="mt-4 w-full text-sm font-semibold text-red-600 hover:underline"
                >
                  Clear Cart
                </button>
              </div>
            </aside>
          </div>
        )}
      </div>

      <DeleteConfirmModal
        open={Boolean(pendingDelete)}
        title={confirmationCopy.title}
        message={confirmationCopy.message}
        confirmLabel={confirmationCopy.confirmLabel}
        busy={deleteBusy}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default OnDemandCartPage;
